// ============================================================================
// api_signalingWs.ts
// Signalisation WebSocket (régie ↔ devices)
// v2.7 — Détection de connexion zombie : fermeture forcée si un ping reste sans pong
// ============================================================================

import { SignalingRefs, SignalingCallbacks, SignalingConfig } from '../../dataTypes/liveStreamManagment/types_signaling';
import {
  handleReturnAnswer, handleReturnIceCandidate,
  closeReturnStreamToDevice, sendReturnStreamToDevice,
  invalidateDeviceNegotiation,
} from './api_webrtcPeerConnection';
import { setDeviceSfuInfo, closeWhepConnection } from './api_whep';
import { DeviceGeoReport } from '../../dataTypes/liveStreamManagment/types_media';

// ============================================================================
// SECTION 0 — Helpers de log ICE et SDP (côté régie)
// ============================================================================

/**
 * Résume un ICE candidate en une ligne lisible.
 * Exemple : "relay udp 62.210.101.187:61476 (via 90.8.255.44:52759) [prio=3|32|255]"
 */
const summarizeIceCandidate = (candidate: RTCIceCandidateInit | RTCIceCandidate | null | undefined): string => {
  if (!candidate) return '(fin gathering — null candidate)';
  const raw = (candidate as RTCIceCandidate).candidate
           ?? (candidate as RTCIceCandidateInit).candidate
           ?? '';
  if (!raw) return '(fin gathering — null candidate)';

  const parts    = raw.split(' ');
  const protocol = parts[2]?.toLowerCase() ?? '?';
  const address  = parts[4] ?? '?';
  const port     = parts[5] ?? '?';
  const typIdx   = parts.indexOf('typ');
  const type     = typIdx >= 0 ? parts[typIdx + 1] : '?';
  const prio     = parts[3] ?? '?';

  let extra = '';
  if (type === 'relay' || type === 'srflx') {
    const raddrIdx = parts.indexOf('raddr');
    const rportIdx = parts.indexOf('rport');
    if (raddrIdx >= 0 && rportIdx >= 0) {
      extra = ` (via ${parts[raddrIdx + 1]}:${parts[rportIdx + 1]})`;
    }
  }

  return `${type} ${protocol} ${address}:${port}${extra} [prio=${prio}]`;
};

/**
 * Résume un SDP en une ligne lisible.
 */
const summarizeSdp = (sdp: string | undefined | null, label: string): string => {
  if (!sdp) return `${label} — SDP vide`;

  const lines = sdp.split('\n');

  const videoCodecs: string[] = [];
  const audioCodecs: string[] = [];
  lines.forEach(l => {
    const m = l.match(/^a=rtpmap:\d+ ([^/]+)/);
    if (!m) return;
    const codec = m[1].toUpperCase();
    if (['H264', 'VP8', 'VP9', 'AV1', 'H265'].includes(codec)) videoCodecs.push(codec);
    else if (['OPUS', 'PCMU', 'PCMA', 'G722', 'ISAC'].includes(codec)) audioCodecs.push(codec);
  });

  const h264Profiles: string[] = [];
  lines.forEach(l => {
    const m = l.match(/profile-level-id=([0-9a-fA-F]{6})/);
    if (m) {
      const pid         = m[1].toLowerCase();
      const profileByte = pid.slice(0, 2);
      const levelByte   = parseInt(pid.slice(4, 6), 16);
      const level       = `${Math.floor(levelByte / 10)}.${levelByte % 10}`;
      const name        = profileByte === '42' ? 'Baseline'
                        : profileByte === '4d' ? 'Main'
                        : profileByte === '64' ? 'High'
                        : `0x${profileByte}`;
      const entry = `${name} ${level} (${pid})`;
      if (!h264Profiles.includes(entry)) h264Profiles.push(entry);
    }
  });

  const directions: string[] = [];
  lines.forEach(l => {
    if (l.startsWith('a=sendrecv'))  directions.push('sendrecv');
    else if (l.startsWith('a=sendonly'))  directions.push('sendonly');
    else if (l.startsWith('a=recvonly'))  directions.push('recvonly');
    else if (l.startsWith('a=inactive'))  directions.push('inactive');
  });

  const mlines         = lines.filter(l => l.startsWith('m=')).map(l => l.split(' ')[0].slice(2));
  const ufragLine      = lines.find(l => l.startsWith('a=ice-ufrag:'));
  const ufrag          = ufragLine ? ufragLine.slice('a=ice-ufrag:'.length).trim() : '?';
  const hasFingerprint = lines.some(l => l.startsWith('a=fingerprint:'));
  const inlineCands    = lines.filter(l => l.startsWith('a=candidate:')).length;

  const videoStr = videoCodecs.length
    ? videoCodecs.join(',') + (h264Profiles.length ? ` [${h264Profiles.join(' | ')}]` : '')
    : 'aucun';
  const audioStr  = audioCodecs.length ? audioCodecs.join(',') : 'aucun';
  const dirStr = Array.from(new Set(directions)).join('/') || '?';
  const mlinesStr = mlines.join('+') || '?';
  const candStr   = inlineCands > 0 ? ` | ${inlineCands} candidats inline` : '';

  return `${label} | m-lines:${mlinesStr} dir:${dirStr} | vidéo:${videoStr} | audio:${audioStr} | ufrag:${ufrag}${candStr} | dtls:${hasFingerprint ? '✅' : '❌'}`;
};

// ============================================================================
// SECTION 1 — Connexion / déconnexion
// ============================================================================

/**
 * Le rattrapage de `device_list` n'est armé que pour le PREMIER device_list
 * reçu après l'ouverture de la WebSocket de la régie. Voir le case
 * 'device_list' pour la raison — c'est ce qui empêche la régie d'offrir à un
 * appareil qui n'a pas encore capturé sa caméra.
 */
let catchUpArmed = false;

/**
 * true dès la première ouverture WS de cet onglet ; permet à `ws.onopen` de
 * distinguer la connexion initiale d'une RECONNEXION après coupure (WS tombée
 * puis rouverte). Réinitialisé à la fermeture volontaire (`disconnectSignaling`) :
 * une reconnexion pour une AUTRE chaîne n'est pas une reprise après coupure.
 *
 * Sert au rattrapage du retour (cf. `__signaling_resumed__` dans le switch
 * ci-dessous) : `device_list` saute délibérément les devices déjà négociés
 * (voir son commentaire), donc rien ne les rattrape après une coupure de LA
 * signalisation elle-même — seul un rechargement complet de la page réparait
 * jusqu'ici le retour resté figé. Constaté en production le 08/09/2026 :
 * redémarrage de chaîne vers 23h, ~11 min de retour figé côté device.
 */
let hasOpenedBefore = false;

/**
 * Cette régie pilote-t-elle ? Suivi ICI, à côté de `catchUpArmed`, parce que
 * c'est ici que le rattrapage se dépense.
 *
 * GARDE-FOU, et non correctif d'un défaut observé : aujourd'hui
 * `broadcast_device_list` (webrtc_signaling.py) n'écrit qu'à
 * `connections["control"]`, donc `device_list` ne parvient JAMAIS à une
 * spectatrice — malgré son nom, ce n'est pas une diffusion. Le rattrapage d'une
 * spectatrice ne peut donc pas se dépenser tout seul.
 *
 * Mais c'est exactement ce qui rend ce garde nécessaire : le jour où l'on
 * enverra `device_list` à toutes les régies — la façon naturelle de corriger la
 * liste figée d'une spectatrice —, une spectatrice dépenserait son rattrapage à
 * offrir dans le vide (le serveur jette ses messages) et n'aurait plus rien
 * quand la main lui reviendrait : multiview noire, et rien pour l'en sortir, le
 * watchdog ne tirant que sur une PC existante. Le garde est ici pour que ce
 * changement-là reste sans danger.
 *
 * Défaut à `true` : régie seule (le cas de loin le plus fréquent), et
 * microservice antérieur qui n'envoie jamais `control_state`.
 */
let regiePilote = true;

export const connectToSignalingServer = (
  config: SignalingConfig,
  refs: Pick<SignalingRefs, 'wsRef' | 'wsReconnectTimeoutRef' | 'wsHbIntervalRef' | 'wsPongTimeoutRef'>,
  callbacks: Pick<SignalingCallbacks, 'addSdpLog'>,
  onMessage: (msg: any) => Promise<void>,
  onReconnect: () => void
): (() => void) => {
  if (refs.wsHbIntervalRef.current) {
    clearInterval(refs.wsHbIntervalRef.current);
    refs.wsHbIntervalRef.current = null;
  }
  if (refs.wsReconnectTimeoutRef.current) {
    clearTimeout(refs.wsReconnectTimeoutRef.current);
    refs.wsReconnectTimeoutRef.current = null;
  }
  if (refs.wsPongTimeoutRef.current) {
    clearTimeout(refs.wsPongTimeoutRef.current);
    refs.wsPongTimeoutRef.current = null;
  }
  console.log(`[SIGNALING] 🔌 Connexion WS → ${config.signalingUrl}`);
  const ws = new WebSocket(config.signalingUrl);
  refs.wsRef.current = ws;

  ws.onopen = () => {
    console.log('[SIGNALING] ✅ WS ouvert');
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '✅ Régie connectée au serveur de signalisation' });
    // Seul le PREMIER device_list de cette connexion sert au rattrapage.
    // Voir le commentaire du case 'device_list'.
    catchUpArmed = true;
    // Même optimisme que le front : on se croit porteur jusqu'à ce que le
    // serveur dise le contraire, dans les millisecondes qui suivent.
    regiePilote  = true;

    if (hasOpenedBefore) {
      console.log('[SIGNALING] 🔄 Reconnexion après coupure — rafraîchissement du retour des devices déjà négociés');
      // Message synthétique — jamais envoyé par le serveur — consommé par
      // handleSignalingMessage comme n'importe quel message reçu : mêmes
      // refs/callbacks que le bouton « Rafraîchir » côté device.
      void onMessage({ type: '__signaling_resumed__' });
    }
    hasOpenedBefore = true;
  };

  ws.onmessage = async (e) => {
    try {
      const msg = JSON.parse(e.data);
      if (msg.type === 'pong') {
        // Preuve que la connexion est bien vivante — annule la fermeture forcée programmée par le ping.
        if (refs.wsPongTimeoutRef.current) {
          clearTimeout(refs.wsPongTimeoutRef.current);
          refs.wsPongTimeoutRef.current = null;
        }
      } else {
        console.log(`[SIGNALING] ↓ message reçu: type=${msg.type}`, msg.device_id ? `device=${msg.device_id}` : '');
      }
      await onMessage(msg);
    } catch (err) {
      console.error('[SIGNALING] Erreur parsing message:', err, e.data);
    }
  };

  ws.onerror = (e) => {
    console.error('[SIGNALING] ❌ WS error:', e);
  };

  ws.onclose = (e) => {
    const codeLabel =
      e.code === 1000 ? 'fermeture normale'      :
      e.code === 1001 ? 'page fermée/navigation' :
      e.code === 1006 ? 'fermeture anormale (réseau/timeout)' :
      e.code === 1008 ? 'violation politique'    :
      `code inconnu`;

    console.warn(
      `[SIGNALING] 🔌 WS fermé — code=${e.code} (${codeLabel})` +
      ` reason="${e.reason || '(vide)'}"` +
      ` wasClean=${e.wasClean}` +
      ` url=${config.signalingUrl}` +
      ` — reconnexion dans 3s`
    );
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `🔄 WS fermé (code=${e.code} ${codeLabel}) — reconnexion dans 3s`,
    });

    if (refs.wsPongTimeoutRef.current) {
      clearTimeout(refs.wsPongTimeoutRef.current);
      refs.wsPongTimeoutRef.current = null;
    }

    refs.wsReconnectTimeoutRef.current = setTimeout(onReconnect, 3000);
  };

  // Heartbeat toutes les 30s pour maintenir la connexion. Un ping sans pong sous 10s
  // signale une connexion zombie (coupure réseau silencieuse, sans frame de fermeture) :
  // readyState resterait OPEN indéfiniment sans ce garde-fou, et onclose/reconnexion
  // ne se déclencheraient jamais — cf. gel du flux retour observé en mode régie.
  refs.wsHbIntervalRef.current = setInterval(() => {
    if (refs.wsRef.current?.readyState === WebSocket.OPEN) {
      refs.wsRef.current.send(JSON.stringify({ type: 'ping' }));

      if (refs.wsPongTimeoutRef.current) {
        clearTimeout(refs.wsPongTimeoutRef.current);
      }
      refs.wsPongTimeoutRef.current = setTimeout(() => {
        console.warn('[SIGNALING] 💀 Aucun pong reçu sous 10s — connexion considérée morte, fermeture forcée');
        refs.wsPongTimeoutRef.current = null;
        refs.wsRef.current?.close();
      }, 10000);
    }
  }, 30000);

  return () => {
    if (refs.wsHbIntervalRef.current) {
      clearInterval(refs.wsHbIntervalRef.current);
      refs.wsHbIntervalRef.current = null;
    }
    if (refs.wsPongTimeoutRef.current) {
      clearTimeout(refs.wsPongTimeoutRef.current);
      refs.wsPongTimeoutRef.current = null;
    }
  };
};

const envoyerControle = (
  refs: Pick<SignalingRefs, 'wsRef'>,
  payload: Record<string, unknown>,
  trace: string,
): boolean => {
  if (refs.wsRef.current?.readyState !== WebSocket.OPEN) {
    console.warn(`[SIGNALING] ${trace} impossible : WS fermée.`);
    return false;
  }
  refs.wsRef.current.send(JSON.stringify(payload));
  console.log(`[SIGNALING] 🎛️ ${trace}`);
  return true;
};

/**
 * DEMANDE la main au porteur — depuis le 23/08/2026, elle ne se prend plus
 * d'autorité. Rien ne bascule ici : le serveur pose la question à la régie qui
 * pilote, et c'est elle qui tranche. Une émission en cours ne change plus de
 * pilote sans que ce pilote l'ait voulu.
 */
export const requestRegieControl = (
  refs: Pick<SignalingRefs, 'wsRef'>,
): boolean => envoyerControle(refs, { type: 'control_request' }, 'Demande de contrôle envoyée');

/**
 * Passe en force, faute de réponse. Le serveur ne l'accepte QUE sur une demande
 * restée sans réponse au-delà du délai de grâce : un refus explicite ferme la
 * demande, et il n'y a alors plus rien à forcer. Le silence, lui, peut être une
 * panne — porteur en veille, onglet gelé — et doit rester rattrapable.
 */
export const forceRegieControl = (
  refs: Pick<SignalingRefs, 'wsRef'>,
): boolean => envoyerControle(refs, { type: 'control_request_force' }, 'Passage en force demandé');

/** Réponse du porteur à une demande. Lui seul est écouté ici. */
export const answerRegieControlRequest = (
  refs: Pick<SignalingRefs, 'wsRef'>,
  accept: boolean,
): boolean => envoyerControle(
  refs, { type: 'control_request_answer', accept },
  accept ? 'Demande de contrôle acceptée' : 'Demande de contrôle refusée',
);

/** La demandeuse renonce : le porteur ne doit pas rester avec la question. */
export const cancelRegieControlRequest = (
  refs: Pick<SignalingRefs, 'wsRef'>,
): boolean => envoyerControle(refs, { type: 'control_request_cancel' }, 'Demande de contrôle retirée');

export const disconnectSignaling =(refs: Pick<SignalingRefs, 'wsRef' | 'wsReconnectTimeoutRef' | 'wsHbIntervalRef' | 'wsPongTimeoutRef'>): void => {
  console.log('[SIGNALING] 🔌 Fermeture WS volontaire');
  // Une fermeture volontaire (changement de chaîne) n'est pas une coupure : la
  // prochaine ouverture, sur cette chaîne ou une autre, ne doit pas déclencher
  // le rattrapage du retour prévu pour une reconnexion subie.
  hasOpenedBefore = false;
  if (refs.wsReconnectTimeoutRef.current) {
    clearTimeout(refs.wsReconnectTimeoutRef.current);
    refs.wsReconnectTimeoutRef.current = null;
  }
  if (refs.wsHbIntervalRef.current) {
    clearInterval(refs.wsHbIntervalRef.current);
    refs.wsHbIntervalRef.current = null;
  }
  if (refs.wsPongTimeoutRef.current) {
    clearTimeout(refs.wsPongTimeoutRef.current);
    refs.wsPongTimeoutRef.current = null;
  }
  // Les gestionnaires sont détachés AVANT close() : `onclose` réarme
  // inconditionnellement une reconnexion à 3 s, et il se déclenche après notre
  // clearTimeout ci-dessus. Sans ce détachement, une fermeture volontaire — celle
  // qu'on fait en changeant de chaîne — ressuscitait la socket qu'on venait de
  // fermer, et la régie se retrouvait avec un canal de contrôle parasite sur la
  // chaîne précédente, indéfiniment.
  const ws = refs.wsRef.current;

  if (ws) {
    ws.onclose   = null;
    ws.onerror   = null;
    ws.onmessage = null;
    ws.onopen    = null;
    ws.close();
  }

  refs.wsRef.current = null;
};

// ============================================================================
// SECTION 2 — Callbacks intercomm optionnels
// ============================================================================

// Malgré son nom, ce bloc est devenu le point d'extension général des messages
// device → régie : il porte déjà les cycles de vie de connexion, et désormais le
// mini-deck du mode Réalisateur.
export interface IntercommSignalingCallbacks {
  onIntercommAnswer?   : (deviceId: string, streamId: string, answer: RTCSessionDescriptionInit) => Promise<void>;
  onIntercommIce?      : (deviceId: string, streamId: string, candidate: RTCIceCandidateInit) => Promise<void>;
  onIntercommSpeaking? : (deviceId: string, active: boolean) => void;
  onDeviceConnected?   : (deviceId: string) => void;
  /**
   * `reason` dit COMMENT l'appareil est parti, ce que la régie ne peut pas
   * deviner seule :
   *
   *   'lost'    — la socket est tombée. Il revient le plus souvent tout seul :
   *               sa place de deck lui est tenue le temps du délai de grâce.
   *   'logout'  — le porteur a cliqué sur Déco, ou la régie l'a déconnecté (le
   *               device traite les deux par le même chemin). Il ne reviendra pas
   *               de lui-même : la place est rendue tout de suite.
   *   'removed' — l'appareil a été supprimé. Il ne reviendra jamais.
   *
   * `undefined` avec un microservice antérieur à ce champ : traité comme 'lost',
   * qui était le comportement unique jusque-là.
   */
  onDeviceDisconnected?: (deviceId: string, reason?: string) => void;

  /**
   * Un device réclame le passage d'une source en final depuis son mini-deck.
   * L'appelant DOIT vérifier que ce device porte bien le rôle Réalisateur :
   * le microservice relaie sans contrôle, n'importe quel device authentifié
   * peut donc émettre ce message.
   */
  onDirectorTake?      : (deviceId: string, sourceId: string) => void;
  /**
   * Un appareil demande l'antenne (`active`), ou la rend.
   *
   * C'est le téléphone qui a créé le direct depuis l'app Kleon. Il ne prend
   * plus l'antenne tout seul — il publierait sur `webrtc_{uuid}`, le chemin où
   * CETTE régie publie son canvas en permanence, et les deux s'évinceraient. Il
   * la demande, et le microservice ne relaie qu'à la régie qui PILOTE : celle
   * qui reçoit ce message est donc bien celle qui doit passer en mode régie
   * avec cet appareil en final. `label` est le nom de l'appareil, pour le dire
   * à l'opérateur.
   */
  onLiveRequest?       : (deviceId: string, active: boolean, label: string) => void;
}

// ============================================================================
// SECTION 3 — Dispatch des messages de signalisation
// ============================================================================

export const handleSignalingMessage = async (
  message: any,
  refs: SignalingRefs,
  callbacks: SignalingCallbacks,
  handlers: {
    connectToDevice:          (device_id: string) => Promise<void>;
    handleRemoteOffer:        (device_id: string, offer: RTCSessionDescriptionInit, meta?: any) => Promise<void>;
    handleRemoteAnswer:       (device_id: string, answer: RTCSessionDescriptionInit) => Promise<void>;
    handleRemoteIceCandidate: (device_id: string, candidate: RTCIceCandidateInit) => Promise<void>;
    cleanupDeadConnection:    (device_id: string) => void;
  },
  intercomm?: IntercommSignalingCallbacks,
): Promise<void> => {
  switch (message.type) {

    // ── Liste des devices connectés ──────────────────────────────────────────
    case 'device_list': {
      callbacks.loadDevices();
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: '📋 device_list reçu' });

      // ── Rattrapage limité au PREMIER device_list de la connexion ──────────
      //
      // Constaté le 15/08/2026 : le rattrapage tel qu'il était écrit CASSAIT
      // toute connexion normale de téléphone. `device_list` est rediffusé dès
      // qu'un appareil ouvre sa WebSocket, c'est-à-dire AVANT qu'il ait envoyé
      // `ready`. Or `ready` n'est pas décoratif : le device l'émet une fois sa
      // caméra capturée, et le microservice attend explicitement ce message
      // avant de notifier la régie (main.py, `await websocket.receive_text()`
      // puis `notify_regie_device_connected`).
      //
      // En offrant sur `device_list`, la régie court-circuitait ce protocole.
      // Le device recevait l'Offer avec `streamRef.current` encore null —
      // getUserMedia en vol — créait sa PeerConnection SANS AUCUNE PISTE et
      // répondait une Answer `dir:inactive`. La négociation « réussissait »
      // (ICE connected, DTLS ✅, badge « Connecté »), mais aucun média ne
      // circulait : pas de `ontrack` côté régie, donc pas d'`injectStream`,
      // donc aucune image dans les mini-vues. Et rien ne s'en remettait — le
      // watchdog ne tire que sur une PC NON connectée, et les Offers suivantes
      // retombaient sur la branche « PC déjà stable » sans jamais rattacher la
      // caméra. Seul un rechargement du device en sortait, pour y retomber.
      //
      // Le rattrapage garde sa raison d'être : le microservice JETTE la
      // notification quand la régie n'est pas connectée — « notify_regie:
      // régie non connectée, notification ignorée » (webrtc_signaling.py) — et
      // ne la rejoue jamais. Mais ce cas ne concerne que les appareils déjà là
      // À L'OUVERTURE de la WebSocket de la régie, donc le premier
      // `device_list` reçu. Tous les suivants sont des diffusions en direct :
      // leurs appareils recevront leur `device_connected` en temps voulu,
      // après `ready`.
      // Une spectatrice ne dépense pas le rattrapage : ses offres seraient jetées
      // par le serveur, et il ne lui en resterait plus quand la main lui
      // reviendrait. Inatteignable tant que `device_list` ne part qu'au porteur —
      // voir `regiePilote` pour la raison de le poser quand même.
      if (!regiePilote) {
        console.log('[SIGNALING] 📋 device_list — régie spectatrice, rattrapage gardé pour la prise de main');
        break;
      }

      if (!catchUpArmed) {
        console.log('[SIGNALING] 📋 device_list — rattrapage non armé (diffusion en direct), rien à faire');
        break;
      }
      catchUpArmed = false;

      // Un device qui se connecte pendant que l'onglet régie est démonté reste
      // affiché « Connecté » (le badge ne reflète que sa WebSocket côté
      // serveur) sans qu'aucune négociation média n'ait eu lieu : rien à
      // l'écran côté régie, device bloqué sur « en attente ». Constaté en
      // production le 14/08/2026. Ces appareils-là ont bien envoyé leur `ready`
      // depuis longtemps — les offrir est sans danger.
      //
      // LIMITÉ AUX DEVICES JAMAIS NÉGOCIÉS — aucune entrée dans
      // remoteConnectionsRef. device_list est rediffusé à chaque connexion ou
      // déconnexion de n'importe quel device (4 sites côté serveur) : élargir
      // le critère à « PeerConnection non connectée » ferait de ce rattrapage
      // un TROISIÈME déclencheur de reconnexion, en concurrence avec le
      // watchdog de makePeerConnection et le scheduler de retry WHEP. Réparer
      // une PeerConnection cassée reste le travail du watchdog, pas d'ici.
      //
      // Séquentiel et non parallèle : connectToDevice attend le gathering ICE
      // complet, et lancer plusieurs négociations de front est précisément ce
      // qui sature le thread principal de la régie.
      const listed: Array<{ device_id?: string; is_connected?: boolean }> =
        Array.isArray(message.devices) ? message.devices : [];

      for (const entry of listed) {
        const id = entry?.device_id;
        if (!id || entry.is_connected === false) continue;
        if (refs.remoteConnectionsRef.current.has(id)) continue;

        console.log(`[SIGNALING] 🩹 device_list: ${id} présent mais jamais négocié → connectToDevice()`);
        callbacks.addSdpLog({
          direction: 'info', type: 'event',
          label: `🩹 Rattrapage: ${id} présent mais jamais négocié`,
          deviceId: id,
        });
        // Parité avec device_connected : sans ça, le device récupérerait la
        // vidéo mais resterait sans intercom. initIntercommForDevice est
        // idempotent (garde knownIntercommDevicesRef).
        intercomm?.onDeviceConnected?.(id);
        // Même parité pour le rôle Réalisateur : ces appareils étaient déjà
        // connectés quand la régie est arrivée, ils n'ont donc jamais déclenché
        // de `device_connected` ici. Sans ce rappel, recharger la régie
        // reverrouillerait l'onglet Réalisateur de tous les devices en place.
        await callbacks.applyDirectorRoleOnConnect?.(id);
      callbacks.applyMetricsLogOnConnect?.(id);
        await handlers.connectToDevice(id);
      }
      break;
    }

    // ── Nouveau device connecté ───────────────────────────────────────────────
    case 'device_connected': {
      const id = message.device_id as string;
      console.log(`[SIGNALING] 📱 device_connected: ${id} → connectToDevice()`);
      callbacks.addSdpLog({ direction: 'received', type: 'event', label: `📱 device_connected: ${id}`, deviceId: id });
      if (message.sfu) setDeviceSfuInfo(id, message.sfu);
      // Le device annonce une session neuve : ce qui se négociait éventuellement
      // pour lui visait la précédente, et son mutex écarterait celle-ci.
      invalidateDeviceNegotiation(id, 'device_connected');
      callbacks.loadDevices();
      intercomm?.onDeviceConnected?.(id);
      // Avant connectToDevice : celui-ci attend le gathering ICE complet et peut
      // échouer. Le rôle Réalisateur est une propriété du device, il n'a pas à
      // dépendre de la réussite d'une négociation vidéo.
      await callbacks.applyDirectorRoleOnConnect?.(id);
      callbacks.applyMetricsLogOnConnect?.(id);
      callbacks.logDeviceConnected?.(
        id,
        typeof message.ip === 'string' ? message.ip : undefined,
        typeof message.user_agent === 'string' ? message.user_agent : undefined,
      );
      await handlers.connectToDevice(id);
      break;
    }

    // ── Reconnexion device (même identifiant) ─────────────────────────────────
    case 'device_reconnected': {
      const id = message.device_id as string;
console.log(`[SIGNALING] 🔄 device_reconnected: ${id} → connectToDevice()`);
      callbacks.addSdpLog({ direction: 'received', type: 'event', label: `🔄 device_reconnected: ${id} — nettoyage PC précédente`, deviceId: id });
      if (message.sfu) setDeviceSfuInfo(id, message.sfu);
      // Idem, et c'est ICI que ça compte : à une passation de régie, la nouvelle
      // porteuse offre au device au moment même où le serveur l'invite à refaire
      // sa session. Sans cette supplantation, la négociation mort-née garde le
      // mutex, celle du retour de l'appareil est écartée, et la régie reste avec
      // un appareil « Connecté » sans image — le retour, lui, passe : il
      // n'emprunte pas ce chemin. Constaté en production le 25/08/2026.
      invalidateDeviceNegotiation(id, 'device_reconnected');
      callbacks.loadDevices();
      //handlers.cleanupDeadConnection(id);
      intercomm?.onDeviceConnected?.(id);
      // Le device a rechargé sa page : son onglet Réalisateur est reverrouillé,
      // il faut le rouvrir comme à une première connexion.
      await callbacks.applyDirectorRoleOnConnect?.(id);
      callbacks.applyMetricsLogOnConnect?.(id);
      callbacks.logDeviceConnected?.(
        id,
        typeof message.ip === 'string' ? message.ip : undefined,
        typeof message.user_agent === 'string' ? message.user_agent : undefined,
      );
      await handlers.connectToDevice(id);
      break;
    }

    // ── Device déconnecté ─────────────────────────────────────────────────────
    case 'device_disconnected': {
		
      console.log('[api_signalingWs] case device_disconnected onDeviceDisconnected=',intercomm?.onDeviceDisconnected);
		
		
      const id = message.device_id as string;
      console.log(`[SIGNALING] 📴 device_disconnected: ${id} → nettoyage PeerConnection`);
      callbacks.addSdpLog({ direction: 'received', type: 'event', label: `📴 device_disconnected: ${id} — nettoyage PC + buffer ICE`, deviceId: id });
      callbacks.loadDevices();
      const disconnectReason = typeof message.reason === 'string' ? message.reason : undefined;
      intercomm?.onDeviceDisconnected?.(id, disconnectReason);
      callbacks.logDeviceDisconnected?.(id);
      closeWhepConnection(id, callbacks); // mode SFU : ferme la session WHEP + stoppe les retries
      handlers.cleanupDeadConnection(id);

      // Vider le buffer ICE de ce device pour stopper tout envoi en cours
      const iceBuf = refs.iceCandidateBufferRef.current.get(id);
      if (iceBuf && iceBuf.length > 0) {
        console.log(`[SIGNALING] 🧹 device_disconnected: vidage buffer ICE de ${id} (${iceBuf.length} candidats)`);
        callbacks.addSdpLog({
          direction: 'info', type: 'event',
          label: `🧹 Buffer ICE vidé pour ${id} (${iceBuf.length} candidats) — device déconnecté`,
          deviceId: id,
        });
        refs.iceCandidateBufferRef.current.set(id, []);
      }
      break;
    }

    // ── Offer reçue (device → régie) ─────────────────────────────────────────
    case 'offer': {
      const from = message.from as string;
      const offerSummary = summarizeSdp(message.payload?.sdp, `Offer ← ${from}`);
      console.log(`[SIGNALING] ↓ ${offerSummary}`);
      callbacks.addSdpLog({ direction: 'received', type: 'offer', label: offerSummary, deviceId: from, sdp: message.payload?.sdp });
      await handlers.handleRemoteOffer(from, message.payload, { supported_codecs: message.supported_codecs });
      break;
    }

    // ── Answer reçue (device → régie) ────────────────────────────────────────
    case 'answer': {
      const from     = message.from as string;
      const streamId = message.stream_id ?? 'main';
      console.log(`[SIGNALING] ↓ answer de ${from} (stream_id=${streamId})`);

      if (streamId === 'return') {
        const answerSummary = summarizeSdp(message.payload?.sdp, `Answer retour ← ${from}`);
        console.log(`[SIGNALING] ↩ ${answerSummary}`);
        callbacks.addSdpLog({ direction: 'received', type: 'answer', label: `↩ ${answerSummary}`, deviceId: from, sdp: message.payload?.sdp });
        await handleReturnAnswer(from, message.payload, callbacks);

      } else if (
        streamId === 'intercomm_regie_to_device' ||
        streamId === 'intercomm_device_to_regie'
      ) {
        console.log(`[SIGNALING] 🎙 answer intercomm de ${from} (${streamId})`);
        callbacks.addSdpLog({ direction: 'received', type: 'answer', label: `🎙 Answer intercomm ← ${from} (${streamId})`, deviceId: from });
        await intercomm?.onIntercommAnswer?.(from, streamId, message.payload);

      } else {
        const answerSummary = summarizeSdp(message.payload?.sdp, `Answer ← ${from}`);
        console.log(`[SIGNALING] ↓ ${answerSummary}`);
        callbacks.addSdpLog({ direction: 'received', type: 'answer', label: answerSummary, deviceId: from, sdp: message.payload?.sdp });
        await handlers.handleRemoteAnswer(from, message.payload);
      }
      break;
    }

    // ── ICE candidate reçu (device → régie) ──────────────────────────────────
    case 'ice-candidate': {
      const from     = message.from as string;
      const streamId = message.stream_id ?? 'main';

      if (streamId === 'return') {
        const summary = summarizeIceCandidate(message.payload);
        console.log(`[SIGNALING] 🧊 ICE retour ← ${from}: ${summary}`);
        callbacks.addSdpLog({ direction: 'received', type: 'event', label: `↩ ICE retour ← ${from}: ${summary}`, deviceId: from });
        await handleReturnIceCandidate(from, message.payload);

      } else if (
        streamId === 'intercomm_regie_to_device' ||
        streamId === 'intercomm_device_to_regie'
      ) {
        const summary = summarizeIceCandidate(message.payload);
        console.log(`[SIGNALING] 🧊 ICE intercomm ← ${from} (${streamId}): ${summary}`);
        await intercomm?.onIntercommIce?.(from, streamId, message.payload);

      } else {
        const summary = summarizeIceCandidate(message.payload);
        console.log(`[SIGNALING] 🧊 ICE ← ${from}: ${summary}`);
        callbacks.addSdpLog({ direction: 'received', type: 'event', label: `🧊 ICE ← ${from}: ${summary}`, deviceId: from });
        await handlers.handleRemoteIceCandidate(from, message.payload);
      }
      break;
    }

    // ── Intercomm speaking (device appuie/relâche le bouton) ─────────────────
    case 'intercomm_speaking': {
      const did    = message.from as string;
      const active = message.active as boolean;
      console.log(`[SIGNALING] 🎙 intercomm_speaking: ${did} active=${active}`);
      callbacks.addSdpLog({ direction: 'received', type: 'event', label: `🎙 intercomm_speaking: ${did} ${active ? 'ON' : 'OFF'}`, deviceId: did });
      intercomm?.onIntercommSpeaking?.(did, active);
      break;
    }

    // ── Mini-deck Réalisateur : le device demande une source en final ────────
    case 'director_take': {
      const did      = message.from as string;
      const sourceId = message.sourceId as string;
      console.log(`[SIGNALING] 🎬 director_take: ${did} → ${sourceId}`);
      callbacks.addSdpLog({ direction: 'received', type: 'event', label: `🎬 director_take: ${sourceId}`, deviceId: did });
      intercomm?.onDirectorTake?.(did, sourceId);
      break;
    }

    // ── Un appareil demande l'antenne, ou la rend ────────────────────────────
    //
    // Le téléphone créateur de l'app Kleon. Le microservice n'a relayé qu'à la
    // régie qui pilote — donc à nous — et attend d'elle qu'elle passe en mode
    // régie avec cet appareil en final. La suite (deck, démarrage) est dans
    // LiveStreamManager.handleLiveRequest.
    case 'live_request': {
      const did    = message.from as string;
      const active = message.active !== false;
      const label  = (message.label as string) || did;
      if (!did) break;
      console.log(`[SIGNALING] 📡 live_request: ${did} (${label}) → ${active ? 'antenne' : 'fin du direct'}`);
      callbacks.addSdpLog({
        direction: 'received', type: 'event',
        label: active ? `📡 ${label} demande l'antenne` : `📡 ${label} rend l'antenne`,
        deviceId: did,
      });
      intercomm?.onLiveRequest?.(did, active, label);
      break;
    }

    // ── Le device redemande son retour d'antenne ─────────────────────────────
    //
    // Bouton « Rafraîchir » du porteur de l'appareil. Lui seul voit que son
    // retour est figé ou noir, et il n'a aucun moyen de le réparer : l'Offer du
    // retour part TOUJOURS de la régie, jamais du device.
    //
    // La fermeture n'est pas facultative : sans elle, sendReturnStreamToDevice
    // voit une PC encore `connected` et la conserve telle quelle — c'est
    // précisément la PC figée qu'on veut remplacer. `forgetPending: false`
    // parce que cette fermeture prépare une tentative, elle ne renonce à rien :
    // si le canvas n'est pas prêt, le device reste dans la file de reprise.
    case 'return_refresh': {
      const did = message.from as string;
      if (!did) break;
      console.log(`[SIGNALING] ↻ return_refresh demandé par ${did}`);
      callbacks.addSdpLog({ direction: 'received', type: 'event', label: `↩ Retour : rafraîchissement demandé par l'appareil`, deviceId: did });
      closeReturnStreamToDevice(did, callbacks, { forgetPending: false });
      await sendReturnStreamToDevice(did, refs, callbacks);
      break;
    }

    // ── Reprise après coupure de LA SIGNALISATION (message synthétique, posé
    // par `ws.onopen` — jamais envoyé par le serveur) ────────────────────────
    //
    // Même geste que `return_refresh`, mais déclenché côté régie plutôt
    // qu'attendu du porteur : `device_list` saute exprès tout device déjà dans
    // `remoteConnectionsRef` (cf. son commentaire), donc un appareil resté
    // "Connecté" pendant que la WS de la régie tombait puis revenait ne
    // récupérait jamais son retour tout seul — jusqu'ici, seul un rechargement
    // complet de la page (ou le bouton côté device) en sortait. Constaté en
    // production le 08/09/2026 : redémarrage de chaîne vers 23h, ~11 min de
    // retour figé côté device malgré un flux remonté normalement.
    //
    // Séquentiel et non parallèle, comme le rattrapage de `device_list` : lancer
    // plusieurs négociations de front sature le thread principal de la régie.
    case '__signaling_resumed__': {
      const ids = Array.from(refs.remoteConnectionsRef.current.keys());
      if (ids.length === 0) break;
      console.log(`[SIGNALING] ↻ Reprise après coupure — rafraîchissement du retour (${ids.length} device(s))`);
      callbacks.addSdpLog({
        direction: 'info', type: 'event',
        label: `↻ Reprise après coupure — rafraîchissement du retour (${ids.length} device(s))`,
      });
      for (const id of ids) {
        closeReturnStreamToDevice(id, callbacks, { forgetPending: false });
        await sendReturnStreamToDevice(id, refs, callbacks);
      }
      break;
    }

    // ── Qualité du lien montant, rapportée par le device ──────────────────────
    //
    // C'est le device qui mesure, et lui seul le peut : en mode SFU la régie tire
    // le flux depuis MediaMTX et ne voit rien du trajet device → SFU. Ce que la
    // régie mesurerait de son côté décrirait le trajet SFU → régie, c'est-à-dire
    // le réseau de la régie, pas celui du téléphone.
    case 'net_quality': {
      const did = message.from as string;
      if (did) callbacks.onDeviceNetworkQuality?.(did, message.level, message.sample);
      break;
    }

    // ── Position et visée du device ──────────────────────────────────────────
    //
    // Comme net_quality, seul le device peut les fournir : ni le flux vidéo ni le
    // serveur ne transportent quoi que ce soit de la position ou de la boussole.
    // Le relevé n'est pas conservé côté serveur — une position figée d'un appareil
    // déconnecté serait un mensonge — donc la régie ne reçoit que du frais.
    case 'device_geo': {
      const did    = message.from as string;
      const report = message.report as DeviceGeoReport | undefined;
      if (did && report && Number.isFinite(report.lat) && Number.isFinite(report.lng)) {
        callbacks.onDeviceGeo?.(did, report);
      }
      break;
    }

    // ── Device rejeté (codec incompatible) ────────────────────────────────────
    case 'device_rejected':
      console.warn(`[SIGNALING] 🚫 device_rejected: ${message.device_id} (raison: ${message.reason})`);
      callbacks.addSdpLog({ direction: 'received', type: 'error', label: `🚫 device_rejected: ${message.device_id} (${message.reason})`, deviceId: message.device_id });
      break;

    // ── Erreur serveur ────────────────────────────────────────────────────────
    case 'error': {
      const targetId = message.target as string | undefined;
      console.error(`[SIGNALING] ❌ Erreur serveur: ${message.error} (target=${targetId ?? '?'})`);
      callbacks.addSdpLog({
        direction: 'received', type: 'error',
        label: `❌ Erreur signaling: ${message.error} (target=${targetId ?? '?'})`,
        ...(targetId ? { deviceId: targetId } : {}),
      });

      // ── Anti-tempête post-déconnexion ─────────────────────────────────────
      // Quand un device se déconnecte, la régie peut avoir des ICE candidates
      // en queue. Chaque envoi vers le target absent génère un target_not_found.
      // On vide le buffer ICE immédiatement pour stopper la cascade.
      if (message.error === 'target_not_found' && targetId) {
        const buf = refs.iceCandidateBufferRef.current.get(targetId);
        if (buf && buf.length > 0) {
          console.warn(`[SIGNALING] 🧹 target_not_found pour ${targetId} — vidage buffer ICE (${buf.length} candidats)`);
          callbacks.addSdpLog({
            direction: 'info', type: 'event',
            label: `🧹 Buffer ICE vidé pour ${targetId} (${buf.length} candidats) — target_not_found`,
            deviceId: targetId,
          });
          refs.iceCandidateBufferRef.current.set(targetId, []);
        } else {
          console.warn(`[SIGNALING] ⚠️ target_not_found pour ${targetId} — buffer ICE déjà vide`);
        }
      }
      break;
    }

    // ── Heartbeat ─────────────────────────────────────────────────────────────
    // ── Contrôle de la régie ────────────────────────────────────────────────
    //
    // Plusieurs régies peuvent être connectées à la même chaîne — une seule
    // pilote. Le serveur envoie control_state à chacune, et control_revoked à
    // celle qui perd la main.
    //
    // Ne PAS confondre avec regie_sync (/{uuid}/regie/sync), qui synchronise
    // l'état du pupitre entre plusieurs écrans et accepte volontairement N
    // clients. Ici il s'agit de savoir qui a le droit de négocier avec les
    // devices : le serveur ignore les messages des régies spectatrices.
    case 'control_state': {
      regiePilote = !!message.is_holder;
      callbacks.setControlState?.({
        isHolder    : !!message.is_holder,
        holderLabel : message.holder_label ?? null,
        holderAddr  : message.holder_addr ?? null,
        regieCount  : message.regie_count ?? 1,
        sessionId   : message.session_id ?? null,
        peers       : Array.isArray(message.peers)
          ? message.peers.map((p: any) => ({
              sessionId: p?.session_id ?? '',
              label    : p?.label ?? 'régie',
              addr     : p?.addr ?? '',
              isHolder : !!p?.is_holder,
            }))
          : [],
      });
      break;
    }

    case 'control_granted': {
      const reason = message.reason ?? 'first';
      console.log(`[SIGNALING] 🎛️ Contrôle obtenu (${reason})`);
      regiePilote = true;

      // ── Reprise de la main : repartir de zéro avec les téléphones ─────────
      //
      // Un device n'a qu'UNE PeerConnection principale : quand l'autre régie a
      // négocié avec lui, le flux s'est DÉPLACÉ, il ne s'est pas dupliqué — nos
      // PC d'alors ont été rompues. Les laisser en place ferait sauter le
      // `continue` du rattrapage (« déjà dans remoteConnectionsRef »), et la
      // multiview resterait noire alors que tous les appareils sont là.
      //
      // On ne touche QU'aux PC déjà rompues. Une PC vivante — régie seule qui
      // reçoit 'first', ou reprise sans qu'aucune autre n'ait négocié — doit
      // être laissée telle quelle : la fermer couperait l'image pour rien.
      for (const [id, conn] of Array.from(refs.remoteConnectionsRef.current.entries())) {
        const etat = (conn as any)?.peerConnection?.connectionState;
        if (etat === 'connected' || etat === 'connecting' || etat === 'new') continue;
        console.warn(`[SIGNALING] 🎛️ Reprise de la main : PC ${id} en « ${etat ?? 'null'} » → nettoyage avant rattrapage`);
        callbacks.addSdpLog({
          direction: 'info', type: 'event',
          label: `🎛️ Reprise de la main : PC rompue nettoyée (${etat ?? 'null'})`,
          deviceId: id,
        });
        handlers.cleanupDeadConnection(id);
      }

      // `grant_control` rediffuse `device_list` juste après ce message : c'est
      // CETTE diffusion-là qui doit rebâtir les négociations, d'où le réarmement.
      catchUpArmed = true;
      callbacks.addSdpLog({
        direction: 'info', type: 'event',
        label: `🎛️ Contrôle de la régie obtenu (${reason})`,
      });
      // Même raison que pour la révocation : appel direct plutôt qu'un drapeau
      // dans l'état, que le control_state diffusé juste après écraserait.
      callbacks.onControlGranted?.(reason);
      callbacks.setControlState?.({
        isHolder: true, holderLabel: null, holderAddr: null, regieCount: 1,
        sessionId: message.session_id ?? null, peers: [],
      });
      break;
    }

    case 'control_revoked': {
      regiePilote = false;
      const takenBy     = message.taken_by ?? 'une autre régie';
      const takenByAddr = message.taken_by_addr ?? null;
      console.warn(`[SIGNALING] 🎛️ Contrôle retiré par ${takenBy}`);
      callbacks.addSdpLog({
        direction: 'received', type: 'error',
        label: `🎛️ Contrôle retiré — repris par « ${takenBy} »${takenByAddr ? ` (${takenByAddr})` : ''}`,
      });
      // L'alerte part par un appel direct, avant toute mise à jour d'état : le
      // control_state que le serveur diffuse juste après écraserait un drapeau
      // porté par l'état, et l'opérateur ne verrait rien.
      callbacks.onControlRevoked?.(takenBy, message.reason ?? 'requested', takenByAddr);
      callbacks.setControlState?.({
        isHolder: false, holderLabel: takenBy, holderAddr: takenByAddr,
        regieCount: 2, sessionId: null, peers: [],
      });
      break;
    }

    // ── Passation de la main ────────────────────────────────────────────────
    // Ces quatre-là encadrent la demande. Aucun ne fait bouger le contrôle : la
    // bascule, quand elle a lieu, passe toujours par control_granted /
    // control_revoked ci-dessus.
    case 'control_requested': {
      const label = message.label ?? 'une autre régie';
      const addr  = message.addr ?? '';
      console.log(`[SIGNALING] 🎛️ « ${label} » (${addr || 'adresse inconnue'}) demande le contrôle`);
      callbacks.addSdpLog({
        direction: 'received', type: 'event',
        label: `🎛️ « ${label} »${addr ? ` (${addr})` : ''} demande le contrôle`,
      });
      callbacks.onControlRequested?.({
        sessionId : message.session_id ?? '',
        label,
        addr,
        graceS    : message.grace_s ?? 20,
      });
      break;
    }

    case 'control_request_pending': {
      callbacks.onControlRequestPending?.(
        message.holder_label ?? null,
        message.grace_s ?? 20,
        message.holder_addr ?? null,
      );
      break;
    }

    case 'control_request_denied': {
      const reason = message.reason ?? 'refused';
      console.warn(`[SIGNALING] 🎛️ Demande de contrôle sans suite (${reason})`);
      callbacks.addSdpLog({
        direction: 'received', type: 'error',
        label: `🎛️ Demande de contrôle sans suite (${reason})`,
      });
      callbacks.onControlRequestDenied?.(reason, message.by ?? null, message.by_addr ?? null);
      break;
    }

    case 'sources_changed': {
      const reason = message.reason ?? 'inconnu';
      console.log(`[SIGNALING] 🔀 sources_changed (${reason}) → rechargement de la liste`);
      callbacks.addSdpLog({
        direction: 'received', type: 'event',
        label: `🔀 Sources externes modifiées (${reason}) — liste rechargée`,
      });
      callbacks.onSourcesChanged?.(reason);
      break;
    }

    case 'duration_notice': {
      const kind    = message.kind === 'limit_reached' ? 'limit_reached' : 'warning';
      const scope   = message.scope === 'recording' ? 'recording' : 'broadcasting';
      const notice  = message.message ?? '';
      console.log(`[SIGNALING] ⏰ duration_notice (${kind}/${scope}): ${notice}`);
      callbacks.addSdpLog({
        direction: 'received', type: 'event',
        label: `⏰ ${notice}`,
      });
      callbacks.onDurationNotice?.(kind, notice, scope);
      break;
    }

    case 'quota_updated': {
      const patch: { durationMaxBroadcasting?: number; durationMaxRecording?: number; quotaDisk?: number } = {};
      if (typeof message.durationMaxBroadcasting === 'number') { patch.durationMaxBroadcasting = message.durationMaxBroadcasting; }
      if (typeof message.durationMaxRecording    === 'number') { patch.durationMaxRecording    = message.durationMaxRecording;    }
      if (typeof message.quotaDisk               === 'number') { patch.quotaDisk               = message.quotaDisk;               }
      console.log('[SIGNALING] 💾 quota_updated:', patch);
      callbacks.addSdpLog({
        direction: 'received', type: 'event',
        label: `💾 Quota mis à jour (${Object.keys(patch).join(', ')})`,
      });
      callbacks.onQuotaUpdated?.(patch);
      break;
    }

    case 'angle_audio_state': {
      callbacks.onAngleAudioState?.(
        Array.isArray(message.ready) ? message.ready.filter((x: unknown): x is string => typeof x === 'string') : [],
      );
      break;
    }

    case 'control_request_cancelled': {
      callbacks.onControlRequestCancelled?.(
        message.label ?? 'une autre régie',
        message.addr ?? null,
      );
      break;
    }

    case 'pong':
      // silencieux
      break;

    default:
      console.warn(`[SIGNALING] Message inconnu: type=${message.type}`, message);
  }
};